import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export type RetrievalJobDoc = {
  status?: string;
  currentStep?: string;
  progress?: number;
  errorMessage?: string;
  mode?: string;
  clientMode?: string;
  model?: string;
  createdAt?: Date | null;
};

export type RetrievalJobEventVM = {
  id: string;
  type: string;
  step?: string;
  label: string;
  durationMs?: number;
  meta?: Record<string, unknown>;
  at: Date | null;
};

function tsToDate(v: unknown): Date | null {
  if (v && typeof v === 'object' && 'toDate' in v && typeof (v as Timestamp).toDate === 'function') {
    try {
      return (v as Timestamp).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

function mapJob(d: DocumentData): RetrievalJobDoc {
  return {
    status: typeof d.status === 'string' ? d.status : undefined,
    currentStep: typeof d.currentStep === 'string' ? d.currentStep : undefined,
    progress: typeof d.progress === 'number' ? d.progress : undefined,
    errorMessage: typeof d.errorMessage === 'string' ? d.errorMessage : undefined,
    mode: typeof d.mode === 'string' ? d.mode : undefined,
    clientMode: typeof d.clientMode === 'string' ? d.clientMode : undefined,
    model: typeof d.model === 'string' ? d.model : undefined,
    createdAt: tsToDate(d.createdAt),
  };
}

/**
 * Subscribes to jobs/{jobId} and jobs/{jobId}/events for live retrieval progress (Thescon AI).
 */
export function useRetrievalJobTimeline(jobId: string | undefined | null) {
  const [job, setJob] = useState<RetrievalJobDoc | null>(null);
  const [events, setEvents] = useState<RetrievalJobEventVM[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!jobId) {
      setJob(null);
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const pollOnce = async () => {
      try {
        const jobRef = doc(db, 'jobs', jobId);
        const eventsQ = query(collection(db, 'jobs', jobId, 'events'), orderBy('timestamp', 'asc'));
        const [jobSnap, eventsSnap] = await Promise.all([getDoc(jobRef), getDocs(eventsQ)]);
        if (cancelled) return;

        if (!jobSnap.exists()) {
          setJob(null);
          setEvents([]);
          setLoading(false);
          return;
        }

        setJob(mapJob(jobSnap.data()));
        const rows: RetrievalJobEventVM[] = eventsSnap.docs.map((d) => {
          const x = d.data();
          return {
            id: d.id,
            type: typeof x.type === 'string' ? x.type : 'info',
            step: typeof x.step === 'string' ? x.step : undefined,
            label: typeof x.label === 'string' ? x.label : '',
            durationMs: typeof x.durationMs === 'number' ? x.durationMs : undefined,
            meta: x.meta && typeof x.meta === 'object' ? (x.meta as Record<string, unknown>) : undefined,
            at: tsToDate(x.timestamp),
          };
        });
        setEvents(rows);
      } catch (err) {
        if (cancelled) return;
        const code = (err as { code?: string } | undefined)?.code;
        // Avoid unhandled promise noise on permission races or missing rules.
        if (code === 'permission-denied' || code === 'unauthenticated') {
          setJob(null);
          setEvents([]);
          setLoading(false);
          return;
        }
        if (process.env.NODE_ENV !== 'production') {
          // Keep signal in dev without crashing the app.
          // eslint-disable-next-line no-console
          console.warn('[useRetrievalJobTimeline] poll failed:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void pollOnce();
    const timer = window.setInterval(() => {
      void pollOnce();
    }, 1200);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [jobId]);

  return { job, events, loading };
}
